import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/auth";

const CATEGORIES = [
 { key: "bug", label: "BUG", hint: "Something broken? Tell me what happened." },
 { key: "idea", label: "IDEA", hint: "Feature you'd kill to have. Spill." },
 { key: "love", label: "LOVE", hint: "Tell me what's fire so I don't break it." },
 { key: "other", label: "OTHER", hint: "Anything else on your mind, drop it." },
];

export default function FeedbackWidget() {
 const { user } = useAuth();
 const loc = useLocation();
 const [open, setOpen] = useState(false);
 const [category, setCategory] = useState("idea");
 const [message, setMessage] = useState("");
 const [email, setEmail] = useState("");
 const [busy, setBusy] = useState(false);
 const [sent, setSent] = useState(false);
 const [err, setErr] = useState("");

 // Reset when reopened
 useEffect(() => {
 if (open) { setSent(false); setErr(""); }
 }, [open]);

 // Never show on the Battle stage itself (visual clutter during broadcast)
 const hideOnBattle = /^\/battles\/[^/]+/.test(loc.pathname);

 const submit = async () => {
 if (message.trim().length < 3) { setErr("Give me at least a few words."); return; }
 setBusy(true); setErr("");
 try {
 await axios.post(`${API}/feedback`, {
 category, message, page: loc.pathname,
 email: (!user && email) ? email : undefined,
 });
 setSent(true);
 setMessage("");
 } catch (e) {
 setErr(e.response?.data?.detail || "Couldn't send — try again.");
 } finally { setBusy(false); }
 };

 if (hideOnBattle) return null;

 return (
 <>
 {/* Floating trigger */}
 <button
 data-testid="feedback-trigger-btn"
 onClick={() => setOpen(true)}
 className="fixed bottom-20 md:bottom-5 left-5 z-40 bg-yt-yellow text-black font-display text-sm tracking-widest px-5 py-3 shadow-brutal hover:bg-yt-red hover:text-white transition-colors"
 title="Send feedback"
 >
 FEEDBACK
 </button>

 {/* Modal */}
 {open && (
 <div
 data-testid="feedback-modal"
 className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
 onClick={() => setOpen(false)}
 >
 <div
 className="w-full max-w-lg bg-yt-surface border-2 border-yt-yellow shadow-brutal-yellow"
 onClick={e => e.stopPropagation()}
 >
 <div className="border-b border-yt-border px-6 py-4 flex justify-between items-center">
 <div>
 <div className="font-mono text-[10px] tracking-[0.4em] text-yt-yellow">DIRECT LINE · RODEO</div>
 <div className="font-display text-2xl tracking-tight">HOLLA AT THE PLATFORM</div>
 </div>
 <button
 data-testid="feedback-close-btn"
 onClick={() => setOpen(false)}
 className="text-yt-muted hover:text-yt-red font-mono text-2xl leading-none"
 >×</button>
 </div>

 {sent ? (
 <div className="p-8 text-center">
 <div className="font-display text-4xl text-yt-judge mb-3">✓ SENT</div>
 <div className="font-mono text-sm text-yt-muted mb-6">Your feedback landed in the Command Center. Rodeo reads every one.</div>
 <div className="flex gap-2 justify-center">
 <button
 data-testid="feedback-another-btn"
 onClick={() => setSent(false)}
 className="border border-yt-border px-4 py-2 font-mono text-xs tracking-widest hover:border-yt-yellow hover:text-yt-yellow"
 >SEND ANOTHER</button>
 <button
 data-testid="feedback-done-btn"
 onClick={() => setOpen(false)}
 className="bg-yt-yellow text-black px-4 py-2 font-mono text-xs tracking-widest"
 >DONE</button>
 </div>
 </div>
 ) : (
 <div className="p-6 space-y-4">
 <div>
 <div className="font-mono text-[10px] tracking-widest text-yt-muted mb-2">WHAT KIND</div>
 <div className="grid grid-cols-4 gap-2">
 {CATEGORIES.map(c => (
 <button
 key={c.key}
 data-testid={`feedback-cat-${c.key}`}
 onClick={() => setCategory(c.key)}
 className={`py-2 px-1 font-mono text-xs tracking-widest border transition-colors ${
 category === c.key
 ? "border-yt-yellow bg-yt-yellow text-black"
 : "border-yt-border text-white hover:border-yt-yellow"
 }`}
 >{c.label}</button>
 ))}
 </div>
 <div className="mt-2 font-mono text-[10px] text-yt-muted">
 {CATEGORIES.find(c => c.key === category)?.hint}
 </div>
 </div>

 <div>
 <div className="font-mono text-[10px] tracking-widest text-yt-muted mb-2">YOUR MESSAGE</div>
 <textarea
 data-testid="feedback-message-input"
 value={message}
 onChange={e => setMessage(e.target.value)}
 rows={5}
 maxLength={2000}
 placeholder="Straight talk. What should Young Tongues change, add, or fix?"
 className="w-full bg-black border border-yt-border p-3 font-mono text-sm text-white focus:border-yt-yellow focus:outline-none"
 />
 <div className="text-right font-mono text-[10px] text-yt-muted">{message.length}/2000</div>
 </div>

 {!user && (
 <div>
 <div className="font-mono text-[10px] tracking-widest text-yt-muted mb-2">EMAIL (OPTIONAL · SO WE CAN REPLY)</div>
 <input
 data-testid="feedback-email-input"
 value={email}
 onChange={e => setEmail(e.target.value)}
 type="email"
 placeholder="you@rap.com"
 className="w-full bg-black border border-yt-border p-3 font-mono text-sm text-white focus:border-yt-yellow focus:outline-none"
 />
 </div>
 )}

 {err && (
 <div data-testid="feedback-error" className="font-mono text-xs text-yt-red border border-yt-red/40 bg-yt-red/5 p-2">
 {err}
 </div>
 )}

 <div className="flex justify-between items-center pt-2">
 <div className="font-mono text-[10px] text-yt-muted">
 {user ? `As ${user.rapper_name}` : "Sent anonymously"} · from {loc.pathname}
 </div>
 <button
 data-testid="feedback-submit-btn"
 onClick={submit}
 disabled={busy || message.trim().length < 3}
 className="bg-yt-red text-white font-display text-lg tracking-wider px-6 py-2 hover:bg-yt-yellow hover:text-black disabled:opacity-40 shadow-brutal shadow-brutal-hover"
 >
 {busy ? "SENDING..." : "▶ SEND"}
 </button>
 </div>
 </div>
 )}
 </div>
 </div>
 )}
 </>
 );
}
